import { useEffect, useState } from 'react';
import { api, getApiErrorMessage } from '../api/client';
import type { MerchantRow, TransactionRow } from '../api/types';
import { mapDbToUi } from '../utils/merchantStatus';

export type AdminStats = {
  totalVolume: number;
  activeMerchants: number;
  totalMerchants: number;
  pendingMerchants: number;
};

function parseAmountDisplay(raw: string): number {
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : 0;
}

function computeStats(merchants: MerchantRow[], transactions: TransactionRow[]): AdminStats {
  const totalVolume = transactions.reduce((sum, tx) => {
    if (tx.status !== 'confirmed') return sum;
    return sum + parseAmountDisplay(tx.amount_display);
  }, 0);

  const statuses = merchants.map((m) => mapDbToUi(m.status));
  const activeMerchants = statuses.filter((s) => s === 'active').length;
  const pendingMerchants = statuses.filter((s) => s === 'pending').length;

  return {
    totalVolume,
    activeMerchants,
    totalMerchants: merchants.length,
    pendingMerchants,
  };
}

export function useAdminStats(enabled: boolean) {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    void Promise.all([api.listMerchants(), api.listTransactions(100)])
      .then(([merchantsRes, txRes]) => {
        if (cancelled) return;
        setStats(computeStats(merchantsRes.merchants, txRes.transactions));
        setError(null);
      })
      .catch((e) => {
        if (!cancelled) setError(getApiErrorMessage(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { stats, error, loading };
}
